import type { IState } from '@/Interfaces/Segments/State.interface';
import { DEFAULT_CREATIVE_FILTERS } from '@/constants/creativeLibrary';
import { defaultFilter, defaultSegment } from '@/constants/segments';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { pluck } from '@/helpers/object';
import { createSlice } from '@reduxjs/toolkit';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '../asyncThunkHandlers';
import { getCreatives, getSegments, getSelectedSegment, updateActiveStatus } from './segments.thunks';

const defaultCreativeFilters = pluck(DEFAULT_CREATIVE_FILTERS, [
  'limit',
  'page',
  'search',
  'sort',
  'statuses',
  'types',
]);

const initialState: IState = {
  filters: defaultFilter,
  creativeFilters: defaultCreativeFilters,

  segments: [],
  segmentsRequestId: DEFAULT_LOADING_REQUEST_ID,

  creatives: [],
  creativesRequestId: null,
  creativesTotal: 0,

  selectedSegment: defaultSegment,
  selectedSegmentRequestId: null,

  activeStatusRequestId: null,
};

export const segmentsSlice = createSlice({
  name: 'segments',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    setCreativesFilters: (state, action) => {
      state.creativeFilters = {
        ...state.creativeFilters,
        ...action.payload,
      };
    },
    resetSelectedSegment: state => {
      state.selectedSegment = defaultSegment;
    },
    resetFilters: state => {
      state.filters = { ...defaultFilter };
    },
    resetCreativesFilters: state => {
      state.creativeFilters = {
        ...defaultCreativeFilters,
      };
    },
    resetSegmentsPageState: () => ({ ...initialState }),
  },
  extraReducers: builder => {
    builder
      .addCase(getSegments.pending, pendingCaseHandler('segments', []))
      .addCase(getSegments.rejected, rejectedCaseHandler('segments'))
      .addCase(getSegments.fulfilled, fulfilledCaseHandler('segments'))
      .addCase(getSelectedSegment.pending, pendingCaseHandler('selectedSegment'))
      .addCase(getSelectedSegment.rejected, rejectedCaseHandler('selectedSegment'))
      .addCase(getSelectedSegment.fulfilled, fulfilledCaseHandler('selectedSegment'))
      .addCase(getCreatives.pending, pendingCaseHandler('creatives', []))
      .addCase(getCreatives.rejected, rejectedCaseHandler('creatives'))
      .addCase(getCreatives.fulfilled, (state, action) => {
        if (state.creativesRequestId === action.meta.requestId) {
          state.creatives = action.payload.data;
          state.creativesTotal = action.payload.total;
          state.creativesRequestId = null;
        }
      })
      .addCase(updateActiveStatus.pending, pendingCaseHandler('activeStatus'))
      .addCase(updateActiveStatus.rejected, rejectedCaseHandler('activeStatus'))
      .addCase(updateActiveStatus.fulfilled, (state, action) => {
        if (state.activeStatusRequestId === action.meta.requestId) {
          state.activeStatusRequestId = null;
          state.segments = state.segments.filter(segment => action.payload.id !== segment.id);
        }
      });
  },
});

export const {
  setFilters,
  setCreativesFilters,
  resetFilters,
  resetSelectedSegment,
  resetSegmentsPageState,
  resetCreativesFilters,
} = segmentsSlice.actions;

export default segmentsSlice.reducer;
